import { useMemo } from 'react';
import { Base64 } from 'js-base64';

import { ApolloQueryResult } from '@apollo/client';
import {
  Exact,
  InputMaybe,
  OrgAnalyticsContext,
  OrgAnalyticsLabelValueFilterInput,
  TeamAnalyticsDataQuery,
  TeamAnalyticsDimensionType,
  useTeamAnalyticsDataQuery
} from 'graphql/types';

export const encodeTeamDataId = ({
  dimensionValue,
  contextValue,
  labels,
  dateRangeValue,
  pageNumber,
  pageSize
}: {
  dimensionValue: TeamAnalyticsDimensionType;
  contextValue: OrgAnalyticsContext;
  labels: OrgAnalyticsLabelValueFilterInput[];
  dateRangeValue: {
    startDate: string;
    endDate: string;
  };
  pageNumber: number;
  pageSize: number;
}): string => {
  const input = {
    context: contextValue,
    dimensionType: dimensionValue,
    dateRange: {
      fromDate: dateRangeValue.startDate,
      toDate: dateRangeValue.endDate
    },
    filterBylabels: labels,
    pageNumber: pageNumber || 1,
    pageSize: pageSize
  };
  const stringInput = JSON.stringify(input);
  const id = Base64.encode(stringInput);
  return id;
};

export const useGetTeamAnalyticsData = (
  id: string | null | undefined
): {
  data: TeamAnalyticsDataQuery | undefined;
  loading: boolean;
  isEmpty: boolean;
  refetch: (
    variables?:
      | Partial<
          Exact<{
            teamAnalyticsResultId?: InputMaybe<string>;
          }>
        >
      | undefined
  ) => Promise<ApolloQueryResult<TeamAnalyticsDataQuery>>;
} => {
  const { loading, data, refetch } = useTeamAnalyticsDataQuery({
    variables: {
      teamAnalyticsResultId: id
    },
    skip: !id
  });

  return useMemo(
    () => ({
      data,
      loading,
      isEmpty: Boolean(
        data?.orgAnalytics?.teamAnalyticsResult.team?.length === 0
      ),
      refetch
    }),
    [data, loading, refetch]
  );
};
